import { API } from "aws-amplify";
import { notification } from "antd";
import { APIS } from "../../utils/apiUtils";
import { notifyError } from "../../utils/notification";

type QueryParams = Record<string, string | number | undefined>;

const getQueryStringParameters = (queryParams: QueryParams) =>
  Object.fromEntries(
    Object.entries(queryParams).filter(
      ([, value]) => value !== undefined && value !== "",
    ),
  );

export interface HierarchyProjectNode {
  projectId: string;
  name: string;
}

export interface HierarchyModuleNode {
  moduleId: string;
  name: string;
}

export interface HierarchyRequirementNode {
  requirementId: string;
  requirementDescription: string;
}

export interface HierarchyScriptNode {
  testcaseId: string;
  scenario: string;
  projectId: string;
  moduleId: string;
  requirementId: string;
}

export interface LazyTreeResponse<T> {
  items: T[];
  nextCursor?: string;
  hasMore: boolean;
}

export const createEditTestSuite = async (testSuite: any): Promise<Boolean> => {
  try {
    return await API.post(APIS.TEST_SUITE_API, "/create", { body: testSuite });
  } catch (error) {
    return false;
  }
};

export const getTestSuites = async (accountId: string): Promise<any> => {
  try {
    const { testSuites } = await API.get(APIS.TEST_SUITE_API, "/get-all", {
      queryStringParameters: { accountId },
    });
    return testSuites;
  } catch (error) {
    return [];
  }
};

export const getTestSuite = async (
  accountId: string,
  testSuiteId: string,
): Promise<any[]> => {
  try {
    const { testSuite } = await API.get(APIS.TEST_SUITE_API, "/get", {
      queryStringParameters: { accountId, testSuiteId },
    });
    return testSuite;
  } catch (error) {
    return [];
  }
};

export const getHierarchyProjectsPage = async (
  accountId: string,
  cursor?: string,
  limit?: number,
): Promise<LazyTreeResponse<HierarchyProjectNode>> => {
  const queryStringParameters = getQueryStringParameters({
    accountId,
    cursor,
    limit,
  });
  const {
    projects = [],
    nextCursor,
    hasMore = false,
  } = await API.get(APIS.PROJECT_API, "/get-test-case-projects", {
    queryStringParameters,
  });
  return {
    items: projects,
    nextCursor,
    hasMore,
  };
};

export const getHierarchyModulesPage = async (
  projectId: string,
  cursor?: string,
  limit?: number,
): Promise<LazyTreeResponse<HierarchyModuleNode>> => {
  const queryStringParameters = getQueryStringParameters({
    projectId,
    cursor,
    limit,
  });
  const {
    modules = [],
    nextCursor,
    hasMore = false,
  } = await API.get(APIS.PROJECT_API, "/get-test-case-modules", {
    queryStringParameters,
  });
  return {
    items: modules,
    nextCursor,
    hasMore,
  };
};

export const getHierarchyRequirementsPage = async (
  moduleId: string,
  cursor?: string,
  limit?: number,
): Promise<LazyTreeResponse<HierarchyRequirementNode>> => {
  const queryStringParameters = getQueryStringParameters({
    moduleId,
    cursor,
    limit,
  });
  const {
    requirements = [],
    nextCursor,
    hasMore = false,
  } = await API.get(APIS.PROJECT_API, "/get-test-case-requirements", {
    queryStringParameters,
  });
  return {
    items: requirements,
    nextCursor,
    hasMore,
  };
};

export const getHierarchyScriptsPage = async (
  projectId: string,
  moduleId: string,
  requirementId: string,
  cursor?: string,
  limit?: number,
): Promise<LazyTreeResponse<HierarchyScriptNode>> => {
  const queryStringParameters = getQueryStringParameters({
    projectId,
    moduleId,
    requirementId,
    cursor,
    limit,
  });
  const {
    scripts = [],
    nextCursor,
    hasMore = false,
  } = await API.get(APIS.PROJECT_API, "/get-test-case-scripts", {
    queryStringParameters,
  });
  return {
    items: scripts,
    nextCursor,
    hasMore,
  };
};

export const deleteTestSuite = async (
  accountId: string,
  testSuiteId: string,
): Promise<Boolean> => {
  try {
    return await API.get(APIS.TEST_SUITE_API, "/delete", {
      queryStringParameters: { accountId, testSuiteId },
    });
  } catch (error) {
    return false;
  }
};

export const deleteTestCaseFromSuite = async (
  accountId: string,
  testSuiteId: string,
  testcaseId: string,
): Promise<Boolean> => {
  try {
    return await API.get(APIS.TEST_SUITE_API, "/testcase/delete", {
      queryStringParameters: { accountId, testSuiteId, testcaseId },
    });
  } catch (error) {
    return false;
  }
};

export const runTestSuite = async (params: any): Promise<boolean> => {
  try {
    await API.post(APIS.EXECUTION_API, "/place-execution", { body: params });
    return true;
  } catch (error: any) {
    console.log(error.message);
    notification.destroy();
    notifyError("Failed to run test suite");
    return false;
  }
};

export const getTestCase = async (
  accountId: string,
  testSuiteId: string,
  testcaseId: string,
): Promise<any> => {
  try {
    return await API.get(APIS.TEST_SUITE_API, "/testcase/get", {
      queryStringParameters: { accountId, testSuiteId, testcaseId },
    });
  } catch (error: any) {
    console.log(error.message);
    return {};
  }
};

export const updateAutomationScript = async (
  requirementId: string,
  testcaseId: string,
  automationScript: string,
): Promise<boolean> => {
  try {
    return await API.post(
      APIS.TEST_SUITE_API,
      "/testcase/update-automation-script",
      {
        queryStringParameters: { requirementId, testcaseId },
        body: { automationScript },
      },
    );
  } catch (error: any) {
    console.log(error.message);
    return false;
  }
};

export const addTestCaseToTestSuite = async (
  accountId: string,
  testSuites: any,
  testcases: any,
): Promise<boolean> => {
  try {
    return await API.post(APIS.TEST_SUITE_API, "/add-test-case-to-test-suite", {
      queryStringParameters: { accountId },
      body: { testSuites, testcases },
    });
  } catch (error: any) {
    console.log(error.message);
    return false;
  }
};

export const getFileSignedUrl = (queryStringParameters: any) =>
  API.get(APIS.MONITOR_API, "/download-log", { queryStringParameters });
